import { z } from 'zod';
import { idSchema } from './common';

/**
 * „Masz coś w innym workspace'ie” (DECISIONS 2026-10-08 [S] „Mam coś w drugim workspace'ie”, [P] „Zakres widoku między
 * workspace'ami”, [P] „Rozliczenie między partnerami — godziny wpisywane raz”). Liczone osobno w każdym INNYM aktywnym
 * członkostwie osoby: w transakcji tamtego workspace'u (RLS) i z jej profilem dostępu w tamtym workspace'ie — osoba
 * widzi tylko to, co zobaczyłaby po przełączeniu. Bez kwot klienta (jedyna kwota to jej własna, nierozliczona kwota
 * partnera z jej wpisów). Do AI (Copilot, brief) i MCP trafiają wyłącznie liczniki z nazwą workspace'u.
 * Usługa: `packages/core/src/modules/workspaces-overview`, tRPC `identity.otherWorkspaces`.
 */

/** Najwięcej innych workspace'ów liczonych w jednym przeglądzie (reszta = `more`). */
export const OTHER_WORKSPACES_LIMIT = 10;
/** Najwięcej pozycji (tytułów) na workspace — tylko na ekranie Dziś, nigdy do AI ani MCP. */
export const OTHER_WORKSPACE_ITEMS_LIMIT = 3;
/** Parametr adresu przełączający workspace (`/inbox?zdarzenie=…&ws=<slug>`): link z push, briefu, innego workspace'u. */
export const WORKSPACE_LINK_PARAM = 'ws';
/**
 * Znacznik wiersza z licznikami innych workspace'ów w instrukcji Copilota (atrapa AI odpowiada z niego na „co mam
 * w innym workspace?”). Po nim wyłącznie liczniki z nazwą workspace'u.
 */
export const OTHER_WORKSPACES_AI_PREFIX = "Inne workspace'y osoby: ";
/** Instrukcja dla Copilota przy licznikach z innych workspace'ów (Copilot działa tylko w bieżącym workspace'ie). */
export const OTHER_WORKSPACES_AI_NOTE =
  "Copilot działa tylko w bieżącym workspace'ie: nie masz narzędzi ani danych innych workspace'ów poza tymi licznikami. Gdy osoba pyta o szczegóły, podpowiedz przełączenie workspace'u (menu z nazwą workspace'u w lewym górnym rogu albo sekcja „W innych workspace'ach” na ekranie Dziś).";

export interface OtherWorkspaceCounts {
  /** moje zadania z terminem na dziś (bez zrobionych) */
  tasksToday: number;
  /** moje zadania po terminie */
  tasksOverdue: number;
  /** akceptacje czekające na moją decyzję (jak plakietka Akceptacji) */
  approvals: number;
  /** pilne nieprzeczytane w moim Inboxie */
  inboxUrgent: number;
  /** otwarte alarmy, które widzę (incydenty + „Pilne” z mojego Inboxu, jak kafelek Dziś „Alarmy”, widok Moje) */
  alarms: number;
  /** moje wyniki AI czekające na odbiór */
  aiResults: number;
}

export const OTHER_WORKSPACE_ITEM_KINDS = ['incident', 'alarm', 'task', 'approval', 'agent_run'] as const;
export type OtherWorkspaceItemKind = (typeof OTHER_WORKSPACE_ITEM_KINDS)[number];

export interface OtherWorkspaceItem {
  kind: OtherWorkspaceItemKind;
  title: string;
  /** krótki podpis: „po terminie”, „na dziś”, „akceptacja”, „alarm”, „wynik AI czeka” */
  meta: string;
  /** ścieżka w aplikacji W TAMTYM workspace'ie (bez parametru `ws`) */
  to: string;
}

/** Moje godziny w tamtym workspace'ie w bieżącym miesiącu (tylko do odczytu; bez kopiowania wpisów). */
export interface OtherWorkspaceHours {
  /** RRRR-MM (Europe/Warsaw) */
  month: string;
  seconds: number;
  entries: number;
  /**
   * Moja nierozliczona kwota partnera (stawka partnera × czas moich wpisów spoza zamkniętego rozliczenia partnera).
   * `null` = brak stawek partnera w tamtym workspace'ie (albo wersja bez rozliczeń partnera).
   */
  partnerUnsettled: string | null;
  currency: string;
  /** raport czasu w tamtym workspace'ie (bez parametru `ws`) */
  reportPath: string;
}

export interface OtherWorkspaceSummary {
  workspace: { id: string; name: string; slug: string };
  counts: OtherWorkspaceCounts;
  /** suma do plakietki: zadania (dziś + po terminie) + akceptacje + max(alarmy, pilne) + wyniki AI */
  waiting: number;
  /** do 3 pozycji (puste, gdy przegląd bez tytułów — AI, MCP) */
  items: OtherWorkspaceItem[];
  /** `null` = bez modułu Czas w tamtym workspace'ie albo przegląd bez godzin (AI, MCP) */
  hours: OtherWorkspaceHours | null;
}

export interface OtherWorkspacesOverview {
  /** inne workspace'y osoby, w kolejności członkostw (bez bieżącego i bez pominiętych po błędzie) */
  workspaces: OtherWorkspaceSummary[];
  /** suma `waiting` (plakietka przy przełączniku workspace'ów) */
  totalWaiting: number;
  /** workspace'y ponad limit (niepoliczone) */
  more: number;
}

export const EMPTY_OTHER_WORKSPACES: OtherWorkspacesOverview = { workspaces: [], totalWaiting: 0, more: 0 };

export function waitingOf(c: OtherWorkspaceCounts): number {
  return c.tasksToday + c.tasksOverdue + c.approvals + Math.max(c.alarms, c.inboxUrgent) + c.aiResults;
}

/** Wejście usługi liczonej w tamtym workspace'ie (wewnętrzne). */
export const otherWorkspaceLocalInput = z
  .object({ titles: z.boolean().default(false), hours: z.boolean().default(false) })
  .strict()
  .default({});

/** `identity.setPrimaryWorkspace` — główny workspace (otwiera się po zalogowaniu); `null` = ostatnio używany. */
export const setPrimaryWorkspaceInput = z.object({ workspaceId: idSchema.nullable() }).strict();
export type SetPrimaryWorkspaceInput = z.infer<typeof setPrimaryWorkspaceInput>;

/** Adres w innym workspace'ie: ścieżka + `ws=<slug>` (link otwiera właściwy workspace). */
export function workspaceLink(path: string, slug: string): string {
  const [base, hash = ''] = path.split('#', 2) as [string, string?];
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}${WORKSPACE_LINK_PARAM}=${encodeURIComponent(slug)}${hash ? `#${hash}` : ''}`;
}

// ── Push z innego workspace'u ───────────────────────────────────────────────

/** Workspace zdarzenia z perspektywy odbiorcy push (liczy worker: `loadPushWorkspaceContext`). */
export interface PushWorkspaceContext {
  name: string;
  slug: string;
  /** osoba ma więcej niż jeden aktywny workspace */
  multi: boolean;
  /** to jej główny workspace (ustawiony albo najstarsze członkostwo) */
  primary: boolean;
}

/**
 * Treść push dla osoby z kilkoma workspace'ami: adres z `ws=<slug>` (otwiera właściwy workspace, także gdy sesja
 * jest w innym), a z workspace'u innego niż główny — nazwa workspace'u w tytule („Fravity OS · Pracownia Marcina”).
 * Jeden workspace = treść bez zmian.
 */
export function pushForWorkspace<T extends { title: string; url: string }>(
  content: T,
  ws: PushWorkspaceContext | null | undefined,
): T {
  if (!ws?.multi) return content;
  return {
    ...content,
    title: ws.primary ? content.title : `${content.title} · ${safeWorkspaceName(ws.name)}`,
    url: workspaceLink(content.url, ws.slug),
  };
}

// ── Tekst liczników (AI, brief, MCP, Dziś) ──────────────────────────────────

function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const last = n % 10;
  const lastTwo = n % 100;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return few;
  return many;
}

/** Liczniki jako tekst: „2 zadania na dziś (1 po terminie), 1 akceptacja, 1 alarm”; pusty = nic nie czeka. */
export function countsText(c: OtherWorkspaceCounts): string {
  const parts: string[] = [];
  if (c.tasksToday > 0) {
    parts.push(
      `${c.tasksToday} ${plural(c.tasksToday, 'zadanie', 'zadania', 'zadań')} na dziś${c.tasksOverdue ? ` (${c.tasksOverdue} po terminie)` : ''}`,
    );
  } else if (c.tasksOverdue > 0) {
    parts.push(`${c.tasksOverdue} ${plural(c.tasksOverdue, 'zadanie', 'zadania', 'zadań')} po terminie`);
  }
  if (c.approvals > 0) parts.push(`${c.approvals} ${plural(c.approvals, 'akceptacja', 'akceptacje', 'akceptacji')}`);
  if (c.alarms > 0) parts.push(`${c.alarms} ${plural(c.alarms, 'alarm', 'alarmy', 'alarmów')}`);
  if (c.inboxUrgent > 0) parts.push(`${c.inboxUrgent} ${plural(c.inboxUrgent, 'pilna', 'pilne', 'pilnych')} w Inboxie`);
  if (c.aiResults > 0) {
    parts.push(`${c.aiResults} ${plural(c.aiResults, 'wynik AI czeka', 'wyniki AI czekają', 'wyników AI czeka')}`);
  }
  return parts.join(', ');
}

/** Nazwa workspace'u do tekstu dla AI: jedna linia, bez znaków sterujących i cudzysłowów, najwyżej 60 znaków. */
export function safeWorkspaceName(name: string): string {
  const clean = name
    // biome-ignore lint/suspicious/noControlCharactersInRegex: celowo usuwamy znaki sterujące z nazwy
    .replace(/[\u0000-\u001f\u007f„”"<>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return (clean.length > 60 ? `${clean.slice(0, 59)}…` : clean) || 'bez nazwy';
}

/**
 * Wiersz dla AI (Copilot, poranny brief): wyłącznie liczniki z nazwą workspace'u, bez tytułów, klientów i kwot.
 * `null` = w innych workspace'ach nic nie czeka.
 */
export function otherWorkspacesAiText(o: Pick<OtherWorkspacesOverview, 'workspaces'>): string | null {
  const lines = o.workspaces
    .map((w) => ({ name: safeWorkspaceName(w.workspace.name), text: countsText(w.counts) }))
    .filter((w) => w.text)
    .map((w) => `W workspace'ie „${w.name}” czeka na Ciebie: ${w.text}.`);
  return lines.length ? lines.join(' ') : null;
}
